import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  BulkCreateEmployeeSalaryAdjustmentDto,
  BulkCreateEmployeesDto,
  CreateEmployeePayrollDto,
  CreateEmployeeSalaryAdjustmentDto,
  EmployeePayrollsApi,
  EmployeeSalaryAdjustmentApi,
  EmployeesApi,
  PayEmployeePayrollDto,
  SendConfirmPushMessageDto,
  ToggleEmployeeStateDtoStateEnum,
  UpdateEmployeeDto,
  UpdateEmployeeRemittanceInfoDto,
} from '../api-client';
import { message } from 'antd';

const employeeSalaryAdjustmentsApi = new EmployeeSalaryAdjustmentApi();
const employeeApi = new EmployeesApi();
const payrollApi = new EmployeePayrollsApi();

export function useEmployees(data: {
  skip?: number;
  take?: number;
  keyword?: string;
}) {
  return useQuery(['employees', data], async () => {
    return new EmployeesApi()
      .employeeControllerFindEmployees(data.skip, data.take, data.keyword)
      .then((res) => res.data);
  });
}

export function useCreateEmployee() {
  const queryClient = useQueryClient();
  return useMutation(
    ['createEmployee'],
    (data: any) => {
      return new EmployeesApi().employeeControllerCreateEmployee(data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('employees');
        queryClient.invalidateQueries('employeeTypes');
      },
    }
  );
}

export function useListEmployees() {
  return useQuery('employees', async () => {
    return new EmployeesApi()
      .employeeControllerListEmployees()
      .then((res) => res.data);
  });
}

export function useUpdateEmployee() {
  const queryClient = useQueryClient();

  return useMutation(
    ['employees', 'update'],
    (data: { id: string } & UpdateEmployeeDto) => {
      const { id, ...updateData } = data;
      return new EmployeesApi().employeeControllerUpdateEmployee(
        id,
        updateData
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('employees');
        queryClient.invalidateQueries('employeeTypes');
      },
    }
  );
}

export function useToggleEmployeeState() {
  const queryClient = useQueryClient();

  return useMutation(
    ['employees', 'toggleState'],
    (data: { id: string; state: ToggleEmployeeStateDtoStateEnum }) => {
      const { id, state } = data;
      return new EmployeesApi().employeeControllerToggleEmployeeState(id, {
        state,
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('employees');
      },
    }
  );
}

export function useEmployeeSalaryAdjustments(
  ...args: Parameters<
    typeof employeeSalaryAdjustmentsApi.employeeSalaryAdjustmentControllerFindAll
  >
) {
  return useQuery(['employeeSalaryAdjustments', ...args], async () => {
    return employeeSalaryAdjustmentsApi
      .employeeSalaryAdjustmentControllerFindAll(...args)
      .then((res) => res.data);
  });
}

export function useCreateSalaryAdjustment() {
  const queryClient = useQueryClient();

  return useMutation(
    ['createEmployeeSalaryAdjustment'],
    (data: CreateEmployeeSalaryAdjustmentDto) => {
      return employeeSalaryAdjustmentsApi.employeeSalaryAdjustmentControllerCreate(
        data
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('employeeSalaryAdjustments');
      },
    }
  );
}

export function useDeleteSalaryAdjustment() {
  const queryClient = useQueryClient();

  return useMutation(
    ['deleteEmployeeSalaryAdjustment'],
    (id: string) => {
      return employeeSalaryAdjustmentsApi.employeeSalaryAdjustmentControllerRemove(
        id
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('employeeSalaryAdjustments');
      },
    }
  );
}

export function useUpdateSalaryAdjustment() {
  const queryClient = useQueryClient();

  return useMutation(
    ['updateEmployeeSalaryAdjustment'],
    (data: { id: string } & CreateEmployeeSalaryAdjustmentDto) => {
      const { id, ...updateData } = data;
      return employeeSalaryAdjustmentsApi.employeeSalaryAdjustmentControllerUpdate(
        id,
        updateData
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('employeeSalaryAdjustments');
      },
    }
  );
}

export function useSalaryAdjustmentTypes() {
  return useQuery('salaryAdjustmentTypes', async () => {
    return employeeSalaryAdjustmentsApi
      .employeeSalaryAdjustmentControllerGetTypes()
      .then((res) => res.data);
  });
}

export function useSalaryAdjustmentFilters() {
  return useQuery('salaryAdjustmentFilters', async () => {
    return employeeSalaryAdjustmentsApi
      .employeeSalaryAdjustmentControllerGetFilters()
      .then((res) => res.data);
  });
}

export function useEmployeeTypes() {
  return useQuery('employeeTypes', async () => {
    return employeeApi
      .employeeControllerListEmployeeTypes()
      .then((res) => res.data);
  });
}

export function useEmployeePayrolls(
  ...args: Parameters<
    typeof payrollApi.employeePayrollControllerFindEmployeePayrolls
  >
) {
  return useQuery(['employeePayrolls', ...args], async () => {
    return payrollApi
      .employeePayrollControllerFindEmployeePayrolls(...args)
      .then((res) => res.data);
  });
}

export function useCreateEmployeePayroll() {
  const queryClient = useQueryClient();

  return useMutation(
    ['createEmployeePayroll'],
    (data: CreateEmployeePayrollDto) => {
      return payrollApi.employeePayrollControllerCreatePayroll(data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('employeePayrolls');
      },
    }
  );
}

export function useVoidEmployeePayroll() {
  const queryClient = useQueryClient();

  return useMutation(
    ['voidEmployeePayroll'],
    (id: string) => {
      return payrollApi.employeePayrollControllerVoidPayroll(id);
    },
    {
      onSuccess: () => {
        message.success('薪資單已作廢');
        queryClient.invalidateQueries('employeePayrolls');
      },
    }
  );
}

export function useSendConfirmationEmployeePayroll() {
  const queryClient = useQueryClient();

  return useMutation(
    ['sendConfirmationEmployeePayroll'],
    (data: SendConfirmPushMessageDto) => {
      return payrollApi.employeePayrollControllerSendConfirmPushMessage(data);
    },
    {
      onSuccess: () => {
        message.success('薪資單已發送確認通知');
        queryClient.invalidateQueries('employeePayrolls');
      },
    }
  );
}

export function usePayEmployeePayroll() {
  const queryClient = useQueryClient();

  return useMutation(
    ['payEmployeePayroll'],
    (data: PayEmployeePayrollDto) => {
      return payrollApi.employeePayrollControllerPayrollPaid(data);
    },
    {
      onSuccess: () => {
        message.success('薪資單已匯款');
        queryClient.invalidateQueries('employeePayrolls');
      },
    }
  );
}

export function useBulkCreateEmployeeSalaryAdjustments() {
  const queryClient = useQueryClient();

  return useMutation(
    ['bulkCreateEmployeeSalaryAdjustments'],
    (data: BulkCreateEmployeeSalaryAdjustmentDto) => {
      return employeeSalaryAdjustmentsApi.employeeSalaryAdjustmentControllerBulkCreate(
        data
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('employeeSalaryAdjustments');
        message.success('薪資調整項目匯入成功');
      },
    }
  );
}

export function useUpdateEmployeeRemittanceInfo() {
  const queryClient = useQueryClient();

  return useMutation(
    ['updateEmployeeRemittanceInfo'],
    (data: { id: string } & UpdateEmployeeRemittanceInfoDto) => {
      const { id, ...updateData } = data;
      return employeeApi.employeeControllerUpdateEmployeeRemittanceInfo(
        id,
        updateData
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('employees');
        message.success('匯款資訊更新成功');
      },
    }
  );
}

export function useBulkCreateEmployee() {
  const queryClient = useQueryClient();

  return useMutation(
    ['bulkCreateEmployee'],
    (data: BulkCreateEmployeesDto) => {
      return employeeApi.employeeControllerBulkCreateEmployees(data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('employees');
        message.success('員工批量創建成功');
      },
      onError: (error) => {
        console.error('員工批量創建失敗:', error);
        message.error('員工批量創建失敗，請稍後再試');
      },
    }
  );
}
